import { getSelectionBoundingBox, isGraphicBlock, isImageBlock, resizeGroup } from '../block';
import { HandleCustomProps } from '../handleCustom';
import { getPageScaleFactor } from '../page';

export const BGRemoverConfig = (cesdkAssetsUrl: string) => {
	const config = {
		ui: { locations: 'canvasMenu' },
		provider: {
			type: '@imgly/background-removal',
			configuration: {
				publicPath: `${cesdkAssetsUrl}/cesdk/background-removal/`,
				model: 'isnet',
				device: 'gpu',
			},
		},
	} as const;
	return config;
};

export async function setupCanvas({ editor, cesdkAssetsUrl }: HandleCustomProps) {
	const BackgroundRemovalPlugin = (await import('@imgly/plugin-background-removal-web')).default;

	const sizePanel = 'selection-size';

	editor.setTranslations({
		en: {
			[`panel.${sizePanel}`]: 'Edit Size',
		},
	});
	//Register the panel to update width and height of the selected blocks
	editor.ui.registerPanel(sizePanel, ({ builder, state, engine }) => {
		const { width, height } = getSelectionBoundingBox(engine);

		if (!width || !height) {
			editor.ui.closePanel(sizePanel);
			return;
		}

		const scaleFactor = getPageScaleFactor(engine);
		const ratio = width / height;

		const { value: localWidth, setValue: setLocalWidth } = state(`${sizePanel}-selectionWidth`, Math.round(width / scaleFactor));
		const { value: localHeight, setValue: setLocalHeight } = state(`${sizePanel}-selectionHeight`, Math.round(height / scaleFactor));
		const { value: lockAspectRatio, setValue: setLockAspectRatio } = state(`${sizePanel}-lockAspectRatio`, true);

		const handleWidthChange = (value: number) => {
			setLocalWidth(Math.round(value));
			if (lockAspectRatio) setLocalHeight(Math.round(value / ratio));
		};
		const handleHeightChange = (value: number) => {
			setLocalHeight(Math.round(value));
			if (lockAspectRatio) setLocalWidth(Math.round(value * ratio));
		};

		const handleUpdate = async () => {
			const selectedIds = engine.block.findAllSelected();
			resizeGroup(editor, selectedIds, localWidth * scaleFactor, localHeight * scaleFactor);
			editor.ui.closePanel(sizePanel);
		};

		builder.Section('selection-size-section', {
			children: () => {
				builder.NumberInput('set-width', {
					inputLabel: 'Width (mm)',
					value: localWidth,
					setValue: handleWidthChange,
				});
				builder.NumberInput('set-height', {
					inputLabel: 'Height (mm)',
					value: localHeight,
					setValue: handleHeightChange,
				});
				builder.Checkbox('lock-aspect-ratio', {
					inputLabel: 'Lock Aspect Ratio',
					value: lockAspectRatio,
					setValue: setLockAspectRatio,
				});

				builder.Button('submit-size-button', {
					label: 'Submit',
					onClick: handleUpdate,
				});
			},
		});
	});

	editor.ui.setPanelFloating(sizePanel, true);
	// editor.ui.registerComponent('width.height.component', ({ builder: { Button }, engine }) => {
	// 	console.log('registering width height component');
	// 	const { width, height } = getSelectionBoundingBox(engine);
	// 	const scaleFactor = getPageScaleFactor(engine);
	// 	console.log(width, height, scaleFactor);

	// 	Button('width.height.text', {
	// 		label: `Width: ${Math.round(width / scaleFactor)}mm Height: ${Math.round(height / scaleFactor)}mm`,
	// 		onClick: () => {
	// 			editor.ui.openPanel(sizePanel);
	// 		},
	// 	});
	// });

	editor.ui.registerComponent('setSizeButton.component', ({ builder: { Button }, engine }) => {
		Button('setSizeButton', {
			label: 'Edit size',
			onClick: () => {
				editor.ui.openPanel(sizePanel);
			},
		});
	});

	editor.ui.registerComponent('downloadImage.component', ({ builder: { Button }, engine }) => {
		const selectedBlockIds = engine.block.findAllSelected();
		if (selectedBlockIds.length !== 1) return;
		if (!isImageBlock(editor.engine.block.getType(selectedBlockIds[0]), editor, selectedBlockIds[0])) return;
		Button('downloadImageButton', {
			icon: '@imgly/Download',
			onClick: () => {
				const block = engine.block.findAllSelected()?.[0];
				if (!block || !isImageBlock(editor.engine.block.getType(block), editor, block)) return;

				//Get the image url
				const fill = editor.engine.block.getFill(block);
				if (!fill) return;
				const sourcSet = editor.engine.block.getSourceSet(fill, 'fill/image/sourceSet');
				const originalSet = sourcSet?.find(set => !set.uri.includes('size=')) || sourcSet[0];
				if (!originalSet) return;
				//Download the image
				const link = document.createElement('a');
				link.href = originalSet.uri;
				link.download = originalSet.uri.split('/').pop() || 'image';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			},
		});
	});

	editor.ui.setCanvasMenuOrder([
		...editor.ui.getCanvasMenuOrder(),
		// 'ly.img.separator',
		// 'width.height.component',
		'ly.img.separator',
		'setSizeButton.component',
	]);

	editor.ui.insertCanvasMenuOrderComponent('ly.img.duplicate.canvasMenu', 'downloadImage.component', 'before');
	editor.addPlugin(BackgroundRemovalPlugin(BGRemoverConfig(cesdkAssetsUrl)));
}
